import type { PaginatedResponseType } from '~/types/api'
import api from './api'

/** El descuento se pacta en dinero ('amount') o como porcentaje ('percent'). */
export type DiscountTypeType = 'amount' | 'percent'

export interface PaymentInfoType {
  bankName?: string
  accountType?: string
  accountNumber?: string
  accountHolder?: string
  holderDocument?: string
}

export type PaymentAccountStatusType = 'draft' | 'sent' | 'paid' | 'overdue'

/** A nombre de quién se emite el cobro: la empresa o el usuario que la crea. */
export type IssuedByType = 'company' | 'user'

export interface PaymentAccountItemType {
  id: number
  paymentAccountId: number
  description: string
  quantity: number
  /** La columna `numeric` llega como texto desde la API: normalizar con toNumber. */
  unitPrice: number | string
  discount: number | string
  discountType: DiscountTypeType
  subtotal: number | string
  notes?: string | null
  order: number
}

export interface PaymentAccountType {
  id: number
  companyId: string
  number: string
  customerId?: number | null
  userId?: number | null
  title: string
  description?: string | null
  status: PaymentAccountStatusType
  issuedBy: IssuedByType
  currency: string
  /** Las columnas `numeric` llegan como texto desde la API: normalizar con toNumber. */
  subtotal: number | string
  taxRate: number | string
  taxAmount: number | string
  /** Lo pactado: pesos o porcentaje, según `discountType`. */
  discount: number | string
  discountType: DiscountTypeType
  /** El descuento ya resuelto a dinero, que es lo que resta del subtotal. */
  discountAmount: number | string
  total: number | string
  issuedAt?: string | null
  dueDate?: string | null
  paidAt?: string | null
  /** Datos bancarios propios; si faltan, el PDF usa los de la empresa. */
  paymentInfo?: PaymentInfoType | null
  notes?: string | null
  terms?: string | null
  items?: PaymentAccountItemType[]
  customer?: { id: number; name: string; document?: string } | null
  createdAt: string
  updatedAt: string
}

export interface CreatePaymentAccountPayloadType {
  customerId?: number
  title: string
  description?: string
  issuedBy?: IssuedByType
  currency?: string
  taxRate?: number
  discount?: number
  discountType?: DiscountTypeType
  dueDate?: string
  paidAt?: string
  paymentInfo?: PaymentInfoType
  notes?: string
  terms?: string
  items: Array<{
    description: string
    quantity?: number
    unitPrice?: number
    discount?: number
    discountType?: DiscountTypeType
    notes?: string
    order?: number
  }>
}

export interface UpdatePaymentAccountPayloadType extends Partial<CreatePaymentAccountPayloadType> {
  status?: PaymentAccountStatusType
}
export interface PaymentAccountListParamsType {
  page?: number
  limit?: number
  number?: string
  title?: string
  status?: string
  customerId?: number
  currency?: string
}

export const paymentAccountService = {
  async list(params?: PaymentAccountListParamsType) {
    const { data } = await api.get('/payment-accounts', { params })
    return data.data as PaginatedResponseType<PaymentAccountType>
  },

  async get(id: number) {
    const { data } = await api.get(`/payment-accounts/${id}`)
    return data.data as PaymentAccountType
  },

  async create(payload: CreatePaymentAccountPayloadType) {
    const { data } = await api.post('/payment-accounts', payload)
    return data.data as PaymentAccountType
  },

  async update(id: number, payload: UpdatePaymentAccountPayloadType) {
    const { data } = await api.put(`/payment-accounts/${id}`, payload)
    return data.data as PaymentAccountType
  },

  async delete(id: number) {
    const { data } = await api.delete(`/payment-accounts/${id}`)
    return data.data as boolean
  },

  /** Elimina varias cuentas de cobro de una vez. Devuelve cuántas se borraron. */
  async bulkDelete(ids: number[]) {
    const { data } = await api.post('/payment-accounts/bulk-delete', { ids })
    return data.data as { deleted: number }
  },

  /** El PDF llega como binario: se pide con responseType blob para no corromperlo. */
  async pdf(id: number) {
    const { data } = await api.get(`/payment-accounts/${id}/pdf`, { responseType: 'blob' })
    return data as Blob
  },

  /** Abre el PDF en una pestaña nueva; el objeto URL se libera al rato. */
  async openPdf(id: number) {
    const blob = await paymentAccountService.pdf(id)
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  },

  async downloadPdf(id: number, number: string) {
    const blob = await paymentAccountService.pdf(id)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `cuenta-cobro-${number}.pdf`
    // El enlace tiene que estar en el documento para que Firefox lo active, y
    // la URL no se puede liberar en el mismo tick o la descarga sale vacía.
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}
